import React from "react";
import BasePage from "../BasePage";
import BGImage from "../../assets/img/Designer.webp";
import imgR from "../../assets/img/img-hero-r.webp";
import imgL from "../../assets/img/img-hero-l.webp";

const LegalNotice: React.FC = () => {
  return (
    <BasePage
      title="Legal Notice - UC Waves"
      description="Find all the legal information regarding UC Waves, including publisher details, hosting information, intellectual property rights, personal data, and liability statements."
      keywords="Legal Notice, UC Waves, Terms, Privacy, Contact"
    >
      <main
        className="relative flex flex-col items-center justify-center pt-28 bg-cover bg-center min-h-screen px-6"
        style={{ backgroundImage: `url(${BGImage})` }}
      >
        {/* Header with images */}
        <div className="flex items-center gap-8 justify-between w-full max-w-5xl mb-10">
          <img src={imgL} alt="Left" className="w-14 md:w-32" />
          <h1 className="text-8xl font-bold text-white text-center drop-shadow-lg">
            Legal Notice
          </h1>
          <img src={imgR} alt="Right" className="w-12 md:w-28" />
        </div>
        <div className="flex items-center gap-8 justify-between w-full max-w-5xl mb-10">
          <img
            src={imgL}
            alt="Hero decorative left"
            width={256} // largeur "md:w-40" en px
            height={256} // même valeur pour un carré
            className="z-[3] w-24 md:w-40 xl:w-64 animate-fade-in-up"
          />
          <h1 className="text-6xl font-bold text-white text-center drop-shadow-lg">
            About Us
          </h1>

          <img
            src={imgR}
            alt="Hero decorative right"
            width={256}
            height={256}
            className="z-[3] w-24 md:w-40 xl:w-64 animate-fade-in-up"
          />
        </div>
        {/* Content container */}
        <div className="bg-white bg-opacity-90 backdrop-blur-md  p-8 max-w-5xl text-gray-800 drop-shadow-xl mb-16">
          <p className="text-sm text-gray-600 mb-8">
            Last updated: <span className="font-semibold">06.12.2025</span>
          </p>
          <p className="mb-6">
            Welcome to the UcWaves platform. This Legal Notice governs your use
            of the website located at{" "}
            <a
              href="https://www.ucwaves.com"
              className="text-primary-dark hover:underline"
            >
              https://www.ucwaves.com
            </a>{" "}
            (the “Website”) and the UcWaves decentralized application (the
            “dApp”). By accessing or using our Website or dApp, you acknowledge
            that you have read, understood, and agreed to the terms outlined in
            this Legal Notice. If you do not agree with any part of this Notice,
            you must discontinue use of the Website and the dApp immediately.
          </p>

          <ol className="list-decimal list-inside space-y-6">
            <li>
              <h2 className="text-2xl font-semibold mb-2">
                No Investment Advice
              </h2>
              <p className="mb-2">
                The content made available through the UcWaves platform,
                including this Website, the dApp, and any affiliated
                documentation, is provided strictly for educational,
                informational, and experimental purposes only. Nothing herein
                constitutes investment advice, financial guidance, trading
                recommendations, or an offer to buy or sell any asset, including
                digital tokens such as NAU, NAUY, or NAUN.
              </p>
              <p>
                We do not act as investment advisors or fiduciaries. You alone
                are responsible for evaluating the merits and risks associated
                with any token transactions or staking decisions made through
                the platform. You should seek independent financial, legal, or
                tax advice from qualified professionals before engaging in any
                blockchain- or cryptocurrency-related activities.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                No Offer or Solicitation
              </h2>
              <p className="mb-2">
                The Website and the dApp do not constitute an offer or
                solicitation to buy or sell securities or any other financial
                instruments in any jurisdiction. The tokens associated with this
                platform—NAU, NAUY, and NAUN—are not securities, and we do not
                intend for them to be perceived or treated as such under
                applicable laws in the United States, European Union, or other
                jurisdictions.
              </p>
              <p>
                Nothing in this platform shall be interpreted as an offer to
                participate in any investment product or as a prospectus or
                offering document under any financial regulatory framework.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Purpose and Function of Tokens
              </h2>
              <p className="mb-2">
                The UcWaves token ecosystem is designed around the principles of
                decentralized opinion sharing, incentivized participation, and
                non-custodial staking. The three tokens (NAU, NAUY, and NAUN)
                serve utility purposes within the dApp:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>
                  <span className="font-semibold">NAU:</span> A base token
                  representing the user’s right to participate in the UcWaves
                  governance model.
                </li>
                <li>
                  <span className="font-semibold">NAUY / NAUN:</span> Tokens
                  minted by burning NAU, expressing the holder's position in a
                  public opinion process (e.g., “Yes” or “No”).
                </li>
              </ul>
              <p className="mt-4">
                None of these tokens are backed by any government or financial
                institution. They do not represent ownership rights, voting
                shares, dividends, or any form of equity in any legal entity or
                organization. The tokens carry no promise of return, yield, or
                appreciation and are not structured to generate profit.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Risks and Volatility
              </h2>
              <p className="mb-2">
                Participation in the digital asset space involves substantial
                risk, including but not limited to:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1 mb-2">
                <li>High volatility of token prices</li>
                <li>Smart contract vulnerabilities or bugs</li>
                <li>Regulatory uncertainty or legal restrictions</li>
                <li>
                  Permanent loss of access to funds due to private key
                  mismanagement
                </li>
                <li>Front-running and market manipulation risks</li>
                <li>Impermanent loss in liquidity provision</li>
              </ul>
              <p>
                We strongly emphasize that cryptocurrencies and decentralized
                finance (DeFi) technologies are experimental and rapidly
                evolving. There is no guarantee that the UcWaves ecosystem or
                its tokens will remain operational, accessible, or valuable in
                the future.
              </p>
              <p className="mt-2">
                You must fully understand and accept these risks before engaging
                with the platform. If you are not comfortable with these risks,
                you should refrain from using the Website or dApp.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Not Intended for Speculation
              </h2>
              <p className="mb-2">
                The UcWaves platform is not designed or intended for speculative
                investment purposes. While token holders may engage in staking
                or interacting with liquidity pools, these activities are not
                intended to generate speculative profits.
              </p>
              <p>
                The utility tokens on the platform exist solely to facilitate
                participation in decentralized governance and incentivized
                opinion expression. Any secondary market value that arises is
                incidental to these functions and is not guaranteed.
              </p>
              <p className="mt-2">
                We discourage speculation and emphasize responsible engagement
                rooted in informed decision-making and alignment with the
                platform’s community goals.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Jurisdictional Limitations
              </h2>
              <p className="mb-2">
                It is your sole responsibility to ensure that your use of the
                UcWaves platform is compliant with local laws and regulations.
                If you reside in a country or region where the use of
                decentralized applications, participation in token economies, or
                possession of digital assets is restricted or prohibited, you
                may not use the Website or dApp.
              </p>
              <p className="mb-2">
                This includes, but is not limited to, individuals or entities
                who are:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1 mb-2">
                <li>Located in countries subject to international sanctions</li>
                <li>
                  Residents of jurisdictions with explicit prohibitions on
                  crypto assets
                </li>
                <li>
                  Engaged in financial services without appropriate licensing
                </li>
              </ul>
              <p>
                We reserve the right to restrict access to the platform where
                necessary to remain compliant with applicable law.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                No Custody or Control
              </h2>
              <p className="mb-2">
                UcWaves is a non-custodial platform. We do not hold or manage
                your funds. All transactions take place via blockchain-based
                smart contracts, and you retain full responsibility for managing
                your wallet, tokens, and private keys.
              </p>
              <p className="mb-2">
                If you lose access to your wallet, we cannot assist you in
                recovering any funds or tokens. You must take proper measures to
                secure your cryptographic credentials, backup your seed phrase,
                and protect your devices.
              </p>
              <p>
                By using our dApp, you agree that you are solely responsible for
                all wallet interactions and any on-chain actions triggered
                through your account.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                User Conduct and Platform Integrity
              </h2>
              <p className="mb-2">Users agree not to:</p>
              <ul className="list-disc list-inside ml-6 space-y-1 mb-2">
                <li>
                  Engage in malicious activities, including hacking,
                  front-running, or spamming
                </li>
                <li>
                  Exploit smart contract vulnerabilities or act in bad faith
                  toward the protocol
                </li>
                <li>
                  Use the platform for money laundering, terrorism financing, or
                  any illegal activity
                </li>
                <li>
                  Impersonate others or misrepresent the nature of transactions
                </li>
              </ul>
              <p>
                We reserve the right to take reasonable steps to protect the
                platform from malicious use, including temporary disabling of
                features, deployment of contract upgrades, and community-led
                governance interventions.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Educational Responsibility and Due Diligence
              </h2>
              <p className="mb-2">
                We urge all users, contributors, and token holders to undertake
                rigorous independent research before participating in any
                blockchain project— including ours.
              </p>
              <p className="mb-2">
                Before buying, staking, or interacting with tokens, you should:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1 mb-2">
                <li>Understand how smart contracts work</li>
                <li>
                  Review our{" "}
                  <a href="#" className="text-primary-dark hover:underline">
                    whitepaper / documentation
                  </a>
                </li>
                <li>Evaluate the risks of interacting with DeFi systems</li>
                <li>Read third-party audits (if available)</li>
                <li>
                  Confirm you understand the irreversible nature of blockchain
                  transactions
                </li>
              </ul>
              <p>
                Remember: blockchain systems are permissionless and immutable.
                Once a transaction is confirmed on-chain, it cannot be undone.
                Mistakes, scams, or poorly understood actions can result in
                permanent loss of assets.
              </p>
              <p className="mt-2">
                We encourage learning, experimentation, and innovation—but not
                at the expense of caution or security.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                No Warranties or Guarantees
              </h2>
              <p>
                The UcWaves platform is provided on an "as-is" and
                "as-available" basis without warranties of any kind, either
                express or implied. We make no representations or warranties
                regarding:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1 mb-2">
                <li>
                  The accuracy, reliability, or completeness of any information
                  provided
                </li>
                <li>
                  The uninterrupted or error-free functionality of the dApp
                </li>
                <li>
                  The future value, utility, or success of the NAU, NAUY, or
                  NAUN tokens
                </li>
                <li>The safety of interactions with smart contracts</li>
              </ul>
              <p>
                Use of the platform is entirely at your own risk. We disclaim
                any liability for losses, disruptions, or damages resulting from
                your use of the Website or dApp.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Limitation of Liability
              </h2>
              <p className="mb-2">
                To the maximum extent permitted by law, UcWaves and its
                contributors, developers, partners, and affiliates shall not be
                liable for any direct, indirect, incidental, special,
                consequential, or punitive damages arising out of or related to:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1 mb-2">
                <li>Use or inability to use the platform</li>
                <li>Errors or mistakes in smart contracts</li>
                <li>Loss of funds, tokens, or access to wallets</li>
                <li>Unauthorized access or hacks</li>
                <li>
                  Regulatory actions or legal limitations affecting your
                  jurisdiction
                </li>
              </ul>
              <p>
                This limitation applies regardless of the theory of liability,
                whether based in contract, tort, negligence, strict liability,
                or otherwise.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Forward-Looking Statements
              </h2>
              <p className="mb-2">
                Some content on the UcWaves platform may contain forward-looking
                statements. These statements are based on current expectations,
                estimates, forecasts, and projections and are not guarantees of
                future performance.
              </p>
              <p className="mb-2">
                They involve assumptions, risks, and uncertainties that are
                difficult to predict and may cause actual results to differ
                materially. Forward-looking statements include, but are not
                limited to:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1 mb-2">
                <li>Future development plans</li>
                <li>Expansion of the platform</li>
                <li>Ecosystem partnerships</li>
                <li>Token utility or use cases</li>
              </ul>
              <p>
                You should not rely on any forward-looking statement as a
                certainty. These statements reflect our perspective as of the
                date they are made and may change without notice.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Community Governance and AuNAUNomy
              </h2>
              <p className="mb-2">
                The long-term goal of the UcWaves project is to move toward
                community-driven governance, where decisions regarding token
                economics, contract upgrades, and policy changes are managed by
                decentralized stakeholders through the use of DAOs or voting
                systems.
              </p>
              <p className="mb-2">
                However, during the early phase of the project, certain controls
                and development responsibilities remain with the founding team
                to ensure secure deployment, responsive iteration, and ecosystem
                growth.
              </p>
              <p>
                We make no representations that decentralization will be
                achieved by a specific date or in a specific form, and
                participation in governance is not equivalent to legal ownership
                or authority over the protocol.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Third-Party Services and Links
              </h2>
              <p className="mb-2">
                The Website and dApp may include links to third-party platforms,
                wallets, or services, including but not limited to:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1 mb-2">
                <li>WalletConnect</li>
                <li>MetaMask, Coinbase Wallet, or other browser extensions</li>
                <li>Decentralized exchanges (DEXs)</li>
                <li>Analytics tools</li>
              </ul>
              <p>
                We do not control these services and are not responsible for
                their security, data handling, or reliability. Use of
                third-party services is entirely at your own risk and governed
                by their respective terms.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Regulatory Compliance and Cooperation
              </h2>
              <p className="mb-2">
                UcWaves will make reasonable efforts to comply with applicable
                regulations and to avoid facilitating unlawful activity.
                However, the responsibility for legal compliance ultimately
                rests with the user.
              </p>
              <p>
                You acknowledge that digital asset regulations are evolving and
                uncertain. Changes in laws or government enforcement priorities
                may impact the legality, functionality, or availability of the
                platform.
              </p>
              <p className="mt-2">
                We reserve the right to update this Legal Notice or restrict
                platform access as necessary to maintain compliance with
                applicable legal obligations.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless UcWaves, its
                developers, affiliates, and partners from and against any and
                all claims, damages, losses, liabilities, costs, and expenses
                (including legal fees) arising out of or related to:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1 mb-2">
                <li>Your use or misuse of the platform</li>
                <li>Violation of these terms</li>
                <li>Violation of applicable laws or regulations</li>
                <li>Infringement of third-party rights</li>
              </ul>
              <p>
                This obligation survives the termination of your use of the
                platform.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Modifications to This Notice
              </h2>
              <p>
                We may update or modify this Legal Notice at any time. You are
                responsible for reviewing the current version each time you use
                the platform. Continued use of the Website or dApp after changes
                constitutes acceptance of the revised Notice.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Contact Information
              </h2>
              <p className="mb-2">
                For legal inquiries or compliance questions, you may contact us
                at:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>
                  Email:{" "}
                  <span className="font-semibold">Contact@ucwaves.com</span>
                </li>
                <li>
                  Website:{" "}
                  <a
                    href="https://www.ucwaves.com"
                    className="text-primary-dark hover:underline"
                  >
                    https://www.ucwaves..com
                  </a>
                </li>
              </ul>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">Final Reminder</h2>
              <p>
                By using the UcWaves Website or dApp, purchasing or interacting
                with NAU, NAUY, or NAUN tokens, or participating in any staking
                or opinion-sharing mechanisms, you confirm that:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1 mb-2">
                <li>You have read and understood this Legal Notice in full.</li>
                <li>You accept all associated risks and responsibilities.</li>
                <li>
                  You understand that you are participating at your own
                  discretion.
                </li>
                <li>
                  You commit to conducting due diligence and protecting your
                  digital assets.
                </li>
              </ul>
              <p className="mt-2">
                We thank you for being part of the UcWaves community. Let’s
                build responsibly and with integrity.
              </p>
            </li>
          </ol>
        </div>
      </main>
    </BasePage>
  );
};

export default LegalNotice;
